import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, Pencil, Send, Trash2, PlayCircle, Library } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import LessonEditDialog from '@/components/lessons/LessonEditDialog';
import AssignLessonDialog from '@/components/lessons/AssignLessonDialog';

const LessonRow = ({ l, onPlay, onEdit, onAssign, onDelete }) => (
  <Card className="border-l-4 border-l-teal-400" data-testid={`library-lesson-${l.id}`}>
    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-lg shrink-0 overflow-hidden">{(l.illustration || '✨').slice(0, 6)}</div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 truncate">{l.title}</p>
        <div className="flex flex-wrap gap-1.5 mt-1 text-xs">
          <Badge variant="outline">Grade {l.grade}</Badge>
          <Badge variant="outline">{l.questions?.length || 0} questions</Badge>
          <Badge variant="outline">{l.say_words?.length || 0} say-it</Badge>
          <Badge variant="outline">{l.trace_words?.length || 0} trace</Badge>
          {l.assigned_count > 0 && <Badge className="bg-teal-100 text-teal-800">Assigned to {l.assigned_count}</Badge>}
          {l.is_owner === false && <Badge className="bg-slate-100 text-slate-700">Shared by your school</Badge>}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={onPlay} data-testid={`library-preview-${l.id}`}><PlayCircle className="w-4 h-4 mr-1" /> Preview</Button>
        {l.is_owner !== false && <Button size="sm" variant="outline" onClick={onEdit} data-testid={`library-edit-${l.id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>}
        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={onAssign} data-testid={`library-assign-${l.id}`}><Send className="w-4 h-4 mr-1" /> Assign</Button>
        {l.is_owner !== false && <Button size="icon" variant="ghost" onClick={onDelete} aria-label="Delete lesson" data-testid={`library-delete-${l.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
      </div>
    </CardContent>
  </Card>
);

const LessonLibraryPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { api } = useAuth();
  const [lessons, setLessons] = useState(null);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = () => api.get('/lessons/library').then((r) => {
    setLessons(r.data.lessons);
    const open = params.get('open');
    const hit = open && r.data.lessons.find((l) => l.id === open);
    if (hit) { setEditing(hit); setParams({}, { replace: true }); }
  }).catch(() => toast.error('Could not load your library'));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (l) => {
    if (!window.confirm(`Delete “${l.title}”? Children it was assigned to will no longer see it.`)) return;
    await api.delete(`/lessons/item/${l.id}`);
    toast.success('Lesson deleted');
    load();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-slate-50" data-testid="lesson-library-page">
      <header className="border-b bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="library-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex-1">
            <h1 className="font-fredoka text-xl font-bold text-slate-900 flex items-center gap-2"><Library className="w-5 h-5 text-teal-600" /> Lesson library</h1>
            <p className="text-xs text-slate-500">Lessons you've created — preview, edit, and assign them.</p>
          </div>
          <Button onClick={() => navigate('/lessons/search')} className="bg-teal-600 hover:bg-teal-700" data-testid="library-new-lesson-btn"><Search className="w-4 h-4 mr-2" /> Find or create</Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-3">
        {!lessons ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
        ) : lessons.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="library-empty">
            <p className="mb-4">No lessons yet. Search a topic like “CVC words” and Zazu will build one.</p>
            <Button onClick={() => navigate('/lessons/search')} className="bg-teal-600 hover:bg-teal-700">Create your first lesson</Button>
          </div>
        ) : lessons.map((l) => (
          <LessonRow key={l.id} l={l} onPlay={() => navigate(`/lesson/preview/${l.id}`)} onEdit={() => setEditing(l)} onAssign={() => setAssigning(l)} onDelete={() => remove(l)} />
        ))}
      </main>
      <LessonEditDialog lesson={editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
      <AssignLessonDialog lesson={assigning} open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)} onAssigned={load} />
    </div>
  );
};

export default LessonLibraryPage;
